<template>
  <ModalComponent
    ref="modal"
    :submitButtonText="
      isSaving
        ? $t('registry_device.production_estimate_override.saving')
        : $t('registry_device.production_estimate_override.save')
    "
    :submit="handleSave"
    :submitDisabled="isSaving || (useManual && !allFieldsFilled)"
    size="xl"
  >
    <template #title>{{
      $t("registry_device.production_estimate_override.title")
    }}</template>

    <template #body>
      <div class="container-fluid">
        <!-- Toggle -->
        <div class="row mb-3">
          <div class="col-12">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="useManualEstimates"
                v-model="useManual"
              />
              <label class="form-check-label" for="useManualEstimates">
                {{
                  useManual
                    ? $t(
                        "registry_device.production_estimate_override.use_manual_estimates"
                      )
                    : $t(
                        "registry_device.production_estimate_override.use_automatic_estimates"
                      )
                }}
              </label>
            </div>
          </div>
        </div>

        <!-- Monthly inputs -->
        <div v-if="useManual" class="row mb-3">
          <div
            v-for="(month, index) in MONTH_LABELS"
            :key="index"
            class="col-md-3 col-sm-4 col-6 mb-2"
          >
            <label class="form-label small text-muted mb-0">{{ month }}</label>
            <input
              type="number"
              class="form-control form-control-sm"
              v-model.number="manualEstimates[index]"
              min="0"
              step="0.1"
            />
          </div>
        </div>

        <!-- Chart -->
        <div class="row mb-3">
          <div class="col-12">
            <StackedBarChart
              v-if="!useManual"
              :chartData="chartData"
              :title="
                $t(
                  'registry_device.production_estimate_override.monthly_production_estimates'
                )
              "
              :subtitle="
                $t(
                  'registry_device.production_estimate_override.automatic_calculated'
                )
              "
              :yAxisTitle="$t('common.mwh')"
              :height="250"
              :showLegend="false"
            />
            <ComparisonBarChart
              v-else
              :autoData="autoProjection"
              :manualData="validManualValues"
              :labels="MONTH_LABELS"
              :autoLabel="
                $t('registry_device.production_estimate_override.automatic')
              "
              :manualLabel="
                $t('registry_device.production_estimate_override.manual')
              "
              :title="
                $t(
                  'registry_device.production_estimate_override.monthly_production_estimates'
                )
              "
              :subtitle="
                $t(
                  'registry_device.production_estimate_override.automatic_vs_manual'
                )
              "
              :yAxisTitle="$t('common.mwh')"
              :height="250"
            />
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import ComparisonBarChart from "@/common/ComparisonBarChart.vue";
import StackedBarChart from "@/common/StackedBarChart.vue";
import { useRegistryDevicesStore } from "@/composables/useRegistryDevicesStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { PALETTE } from "@/composables/chartPalette";
import type { StackableDataset } from "@/types/ChartData";
import type { RegistryDevice } from "@/types";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  companyId: number;
}>();

const emit = defineEmits<{
  saved: [deviceId: number];
}>();

const store = useRegistryDevicesStore();
const modal = useTemplateRef<InstanceType<typeof ModalComponent>>("modal");
const { t } = useI18n();
const { globalWarningHandler, globalErrorHandler } = useGlobalHandlers();

const MONTH_LABELS = computed(() =>
  Array.from({ length: 12 }, (_, i) => t(`common.months_abbrev.${i + 1}`))
);

const device = ref<RegistryDevice | null>(null);
const useManual = ref(false);
const manualEstimates = ref<(number | null)[]>(new Array(12).fill(null));
const autoProjection = ref<number[]>(new Array(12).fill(0));
const isSaving = ref(false);

const allFieldsFilled = computed(() => {
  return manualEstimates.value.every(
    (v) => v !== null && v !== undefined && !isNaN(v) && v >= 0
  );
});

const validManualValues = computed(() => {
  return manualEstimates.value.map((v) =>
    v !== null && v !== undefined && !isNaN(v) && v >= 0 ? v : 0
  );
});

const chartData = computed(() => ({
  labels: MONTH_LABELS.value,
  datasets: [
    {
      label: t("registry_device.production_estimate_override.automatic"),
      data: autoProjection.value,
      backgroundColor: PALETTE.semanticOverrides.status.Available,
      borderColor: PALETTE.semanticOverrides.status.Available,
      borderWidth: 1,
    },
  ] as StackableDataset[],
}));

const loadProjectionDetail = async () => {
  if (!device.value) return;
  try {
    const results = await store.fetchDeviceProjectionDetails(props.companyId, [
      device.value.id,
    ]);
    const detail = results[device.value.id];
    if (!detail) throw new Error("No projection data returned");
    autoProjection.value = MONTH_LABELS.value.map(
      (_, i) => detail.auto_projection[String(i + 1)] ?? 0
    );

    if (detail.has_override) {
      useManual.value = true;
      manualEstimates.value = MONTH_LABELS.value.map(
        (_, i) => Math.round((detail.projection[String(i + 1)] ?? 0) * 10) / 10
      );
    } else {
      useManual.value = false;
      manualEstimates.value = autoProjection.value.map(
        (v) => Math.round(v * 10) / 10
      );
    }
  } catch (err) {
    globalErrorHandler(err);
  }
};

const handleSave = async () => {
  if (!device.value) return;
  isSaving.value = true;
  try {
    if (useManual.value && !allFieldsFilled.value) return;
    const result = await store.setProductionOverride(
      props.companyId,
      device.value.id,
      useManual.value ? validManualValues.value : null
    );
    if (result.feasibility && !result.feasibility.feasible) {
      const feasibilityDetails = result.feasibility.details;
      globalWarningHandler(
        t(
          "registry_device.production_estimate_override.feasibility_warning_title"
        ),
        t("registry_device.production_estimate_override.feasibility_warning", {
          period: feasibilityDetails?.period_label,
          demand: feasibilityDetails?.demand_mwh,
          shortfall: feasibilityDetails?.shortfall_mwh,
        })
      );
    }
    emit("saved", device.value.id);
    modal.value?.toggleModal();
  } catch (err) {
    globalErrorHandler(err);
  } finally {
    isSaving.value = false;
  }
};

const show = async (d: RegistryDevice) => {
  device.value = d;
  await loadProjectionDetail();
  modal.value?.toggleModal();
};

defineExpose({ show });
</script>
